import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import { Platform, StyleSheet, Text, StyleProp, ViewStyle, TextStyle } from 'react-native';
import { MB_formDataCreator } from '@/utils/general/form-data-creator';
import { isMobile, isMobileApp } from '@/utils/general/device-info';
import { MB_Button } from '@/components/general/button';
import { MB_Modal } from '@/components/general/modal';
import { COLORS, SERVER_ERROR_CODES, STRING_CONSTANTS } from './MB_ResourceUploaderConstants';
import { MB_ResourcePickerResource, MB_ResourcePickerUI, MB_RESOURCE_PICKER_TYPE } from '@/components/general/resource-picker';
import { MB_imageCompressorUtils } from '@/utils/general/image-compressor';
import { MB_RESOURCE_PICKER_CONSTANTS } from '@/components/general/resource-picker';
import { MB_fileManagement } from '@/utils/general/file-management';
import * as mime from '@/utils/general/mime-types';

interface MB_ResourceUploaderStyleProps {
  /**
   * Style of the wrapper that wraps the resource uploader.
   */
  wrapperStyle?: StyleProp<ViewStyle>;
  uploadButtonStyle?: StyleProp<ViewStyle>;
  uploadButtonTextStyle?: StyleProp<TextStyle>;
  previewContainerStyle?: StyleProp<ViewStyle>;
  previewImageBackgroundStyle?: StyleProp<ViewStyle>;
  modalTitleStyle?: StyleProp<TextStyle>;
  modalTitleWrapperStyle?: StyleProp<ViewStyle>;
  dragAndDropAndProcessingResourceTextStyle?: StyleProp<TextStyle>;
  cameraOrGalleryModalCloseButtonXProps?: { size?: number; color?: string };
  /**
   * "Browse" button style
   */
  dragAndDropAndProcessingResourceButtonColor?: string;
}

interface MB_ResourceUploaderFunctionProps {
  formData: FormData;
  blob: Blob | undefined;
  uri: string;
  fileSize: number;
  mimeType: string;
  fileName: string | undefined;
}

interface MB_ResourceUploaderProps extends MB_ResourceUploaderStyleProps {
  isVisible: boolean;
  onDismiss: (uploadedSuccessfully: boolean) => void;

  /**
   * blob is sent when running on web, no blob for mobile.
   * uri is sent for both web and mobile
   */
  uploaderFunction: (uploadProgressListener: (percentCompleted: number) => void) => (props: MB_ResourceUploaderFunctionProps) => Promise<void>;
  pageTitle: string;
  uploadButtonTitle: string;
  resourcePickerType: MB_RESOURCE_PICKER_TYPE;
  children?: ReactNode;
  allowedFileExtensions?: string; // In format '.jpeg,.jpg,.png'

  /**
   * This is used when picker type is anyFile. Expected to be an array of mime types such as
   * 'image/*', 'image/png', 'application/pdf'.
   */
  allowedDocumentPickerTypes?: string[];
  cameraOrGalleryModalCloseButtonXProps?: { size?: number; color?: string };
  cameraorGalleryIconColor?: string;
  previewColor?: string;
  cropAspectRatio?: [number, number];
  /**
   * The maximum duration of the video in seconds.
   */
  maxDuration?: number;
}

const MB_ResourceUploaderSetDefaultStyles = (props: MB_ResourceUploaderStyleProps) => {
  defaultProps = props;
};

let defaultProps: MB_ResourceUploaderStyleProps | undefined;

const MB_ResourceUploaderUI = ({
  children,
  onDismiss,
  uploaderFunction,
  uploadButtonTitle,
  resourcePickerType,
  wrapperStyle,
  modalTitleStyle,
  modalTitleWrapperStyle,
  uploadButtonStyle,
  uploadButtonTextStyle,
  previewContainerStyle,
  previewImageBackgroundStyle,
  dragAndDropAndProcessingResourceButtonColor,
  allowedFileExtensions,
  allowedDocumentPickerTypes,
  dragAndDropAndProcessingResourceTextStyle,
  cameraOrGalleryModalCloseButtonXProps,
  cameraorGalleryIconColor,
  previewColor,
  cropAspectRatio,
  maxDuration,
}: Omit<MB_ResourceUploaderProps, 'isVisible' | 'pageTitle'>) => {
  const [resource, setResource] = useState<MB_ResourcePickerResource | undefined>();
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [uploadPercentage, setUploadPercentage] = useState<number>(0);

  // Delete the resource file on mobile if pop-up is closing and resource is set.
  useEffect(() => {
    return () => {
      if (resource && isMobileApp) {
        // TODO: Mobile: Do we want to keep these?
        //MB_fileManagement.deleteFile(resource.uri);
      }
    };
  }, [resource]);

  async function uploadResource(retryCount: number) {
    if (!resource) {
      return;
    }

    const formDataKey = resourcePickerType === MB_RESOURCE_PICKER_TYPE.image ? 'image' : resourcePickerType === MB_RESOURCE_PICKER_TYPE.video ? 'video' : 'file';
    const formData = await MB_formDataCreator.createFormData({
      dataForFile: [{ blob: resource.blob, uri: resource.uri, key: formDataKey }],
    });

    const fileSize = await MB_fileManagement.getFileSize(resource.blob ?? resource.uri);

    let mimeType = resource.blob?.type ?? mime.lookup(resource.uri);
    if (!mimeType) {
      mimeType = 'application/octet-stream';
    }

    const uploaderFunc = uploaderFunction(setUploadPercentage);

    uploaderFunc({ formData, blob: resource.blob, uri: resource.uri, fileSize, mimeType, fileName: resource.fileName ?? (resource.blob as any)?.name })
      .then(() => {
        setResource(undefined);
        setIsLoading(false);
        onDismiss(true);
      })
      .catch((error: any) => {
        // TODO: Note: Android upload sometimes fail unexpectedly the first time and work just fine on second try.
        // Was unable to figure out why. Maybe someone else with fresh ideas can address this at some point.
        if (Platform.OS === 'android' && retryCount < 2 && error?.errorCode === SERVER_ERROR_CODES.UNKNOWN_ERROR) {
          console.error('Error uploading resource. Retrying');
          setTimeout(() => {
            uploadResource(retryCount + 1);
          }, 100);
          return;
        }

        console.log('Error when uploading media', error);
        setIsLoading(false);
        if (error?.errorCode === SERVER_ERROR_CODES.LIMIT_FILE_SIZE) {
          setErrorMessage(
            MB_imageCompressorUtils.fileIsTooLargeString(
              resourcePickerType === MB_RESOURCE_PICKER_TYPE.image ? MB_RESOURCE_PICKER_CONSTANTS.MAX_IMAGE_SIZE_AFTER_COMPRESSION : MB_RESOURCE_PICKER_CONSTANTS.MAX_VIDEO_SIZE
            )
          );
        } else if (error.message) {
          setErrorMessage(error.message);
        } else {
          setErrorMessage(STRING_CONSTANTS.SOMETHING_WENT_WRONG_PLEASE_TRY_AGAIN);
        }
      });
  }

  const onUploadPress = async () => {
    setErrorMessage('');
    try {
      if (!resource) {
        return;
      }

      setIsLoading(true);

      uploadResource(0);
    } catch (error) {
      console.error('Error when trying to upload resource in MB_ResourcePickerAndUploader', error);
    }
  };

  /*
    onResourceChanged: (newResource: MB_ResourcePickerResource | undefined) => void,
    uploadPercentage: number,
    isLoading: boolean,
    setErrorMessage: (message: string | undefined) => void,
    */

  return (
    <>
      <MB_ResourcePickerUI
        resourcePickerType={resourcePickerType}
        resource={resource}
        onResourceChanged={setResource}
        uploadPercentage={uploadPercentage}
        isLoading={isLoading}
        setErrorMessage={setErrorMessage}
        containerStyle={previewContainerStyle}
        previewImageBackgroundStyle={previewImageBackgroundStyle}
        dragAndDropAndProcessingResourceButtonColor={dragAndDropAndProcessingResourceButtonColor}
        allowedFileExtensions={allowedFileExtensions}
        allowedDocumentPickerTypes={allowedDocumentPickerTypes}
        dragAndDropAndProcessingResourceTextStyle={dragAndDropAndProcessingResourceTextStyle}
        cameraorGalleryIconColor={cameraorGalleryIconColor}
        cameraOrGalleryModalCloseButtonXProps={cameraOrGalleryModalCloseButtonXProps}
        previewColor={previewColor}
        cropAspectRatio={cropAspectRatio}
        wrapperStyle={wrapperStyle}
        modalTitleStyle={modalTitleStyle}
        modalTitleWrapperStyle={modalTitleWrapperStyle}
        uploadButtonStyle={uploadButtonStyle}
        uploadButtonTextStyle={uploadButtonTextStyle}
        maxDuration={maxDuration}
      />
      <Text style={styles.errorText}>{errorMessage}</Text>
      {children}
      <MB_Button
        title={uploadButtonTitle}
        onPress={onUploadPress}
        style={uploadButtonStyle ?? styles.uploadButton}
        textStyle={uploadButtonTextStyle}
        loading={isLoading}
        disabled={isLoading || !resource}
      />
    </>
  );
};

const MB_ResourceUploaderInternal = ({
  children,
  isVisible,
  onDismiss,
  uploaderFunction,
  pageTitle,
  uploadButtonTitle,
  resourcePickerType,
  wrapperStyle,
  uploadButtonStyle,
  uploadButtonTextStyle,
  previewContainerStyle,
  dragAndDropAndProcessingResourceButtonColor,
  previewImageBackgroundStyle,
  allowedFileExtensions,
  allowedDocumentPickerTypes,
  modalTitleWrapperStyle,
  modalTitleStyle,
  dragAndDropAndProcessingResourceTextStyle,
  cameraorGalleryIconColor,
  cameraOrGalleryModalCloseButtonXProps,
  previewColor,
  maxDuration,
}: MB_ResourceUploaderProps) => {
  const childWrapperStyle = useMemo(() => {
    if (wrapperStyle) {
      return wrapperStyle;
    }
    if (!isMobile) {
      return {
        paddingHorizontal: resourcePickerType === MB_RESOURCE_PICKER_TYPE.image ? 186 : 118,
      };
    }
  }, [resourcePickerType, wrapperStyle]);

  return (
    <MB_Modal
      isVisible={isVisible}
      onDismiss={() => onDismiss(false)}
      childrenWrapperStyle={childWrapperStyle}
      closeButtonXProps={cameraOrGalleryModalCloseButtonXProps}
      titleWrapperStyle={modalTitleWrapperStyle}
      titleStyle={modalTitleStyle}
      title={pageTitle !== '' ? pageTitle : undefined}
    >
      <MB_ResourceUploaderUI
        onDismiss={onDismiss}
        uploaderFunction={uploaderFunction}
        uploadButtonTitle={uploadButtonTitle}
        resourcePickerType={resourcePickerType}
        uploadButtonStyle={uploadButtonStyle}
        uploadButtonTextStyle={uploadButtonTextStyle}
        previewContainerStyle={previewContainerStyle}
        dragAndDropAndProcessingResourceButtonColor={dragAndDropAndProcessingResourceButtonColor}
        previewImageBackgroundStyle={previewImageBackgroundStyle}
        allowedFileExtensions={allowedFileExtensions}
        allowedDocumentPickerTypes={allowedDocumentPickerTypes}
        dragAndDropAndProcessingResourceTextStyle={dragAndDropAndProcessingResourceTextStyle}
        cameraorGalleryIconColor={cameraorGalleryIconColor}
        cameraOrGalleryModalCloseButtonXProps={cameraOrGalleryModalCloseButtonXProps}
        previewColor={previewColor}
        wrapperStyle={childWrapperStyle}
        modalTitleStyle={modalTitleStyle}
        modalTitleWrapperStyle={modalTitleWrapperStyle}
        maxDuration={maxDuration}
      >
        {children}
      </MB_ResourceUploaderUI>
    </MB_Modal>
  );
};

const MB_ResourceUploader = (props: MB_ResourceUploaderProps) => {
  if (defaultProps) {
    const newProps: MB_ResourceUploaderStyleProps = {};
    Object.keys(defaultProps).forEach(key => {
      const stylePropsKey = key as keyof MB_ResourceUploaderStyleProps;
      if (stylePropsKey === 'cameraOrGalleryModalCloseButtonXProps') {
        newProps[stylePropsKey] = props[stylePropsKey] ? { ...defaultProps?.[stylePropsKey], ...props[stylePropsKey] } : defaultProps?.[stylePropsKey];
      } else if (stylePropsKey === 'dragAndDropAndProcessingResourceButtonColor') {
        newProps[stylePropsKey] = props[stylePropsKey] ? props[stylePropsKey] : defaultProps?.[stylePropsKey];
      } else {
        newProps[stylePropsKey] = props[stylePropsKey] ? [defaultProps![stylePropsKey], props[stylePropsKey]] : defaultProps![stylePropsKey];
      }
    });

    return <MB_ResourceUploaderInternal {...props} {...newProps} />;
  }

  return <MB_ResourceUploaderInternal {...props} />;
};

export { MB_ResourceUploader, MB_ResourceUploaderSetDefaultStyles };
export type { MB_ResourceUploaderProps, MB_ResourceUploaderFunctionProps };

const styles = StyleSheet.create({
  uploadButton: {
    width: 268,
    height: 62,
    marginTop: 32,
    backgroundColor: COLORS.plum,
  },
  errorText: {
    fontWeight: 'normal',
    fontSize: 12,
    textAlign: 'center',
    color: COLORS.errorColor,
    marginTop: 26,
    maxWidth: 386,
  },
});
